<template>
    <!-- Proposal reply (via the Confirm/Cancel buttons) -->
    <div
        v-if="proposalReplyKind(entry, index)"
        class="proposal-reply-row"
        :class="proposalReplyKind(entry, index)"
    >
        <i
            :class="
                proposalReplyKind(entry, index) === 'confirmed'
                    ? 'pi pi-check-circle'
                    : 'pi pi-times-circle'
            "
        />
        <span class="proposal-reply-text">
            {{ proposalReplyLabel(entry, index) }}
        </span>
        <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
    </div>

    <!-- Contribution (user or external agent) -->
    <div
        v-else-if="entry.type === 'contribution'"
        class="entry-bubble contribution-bubble"
    >
        <div class="bubble-header">
            <span class="source-name">{{ entry.source.name }}</span>
            <Tag
                v-if="entry.source.type === 'external_agent'"
                value="external agent"
                severity="secondary"
                class="source-tag"
            />
            <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
        </div>
        <ThreadMarkdown class="bubble-content" :content="entry.content ?? ''" />
        <div v-if="files.length" class="bubble-files">
            <div v-for="file in files" :key="file.fileId" class="bubble-file">
                <button
                    type="button"
                    class="bubble-file-open"
                    :title="`Open ${file.filename} in a new tab`"
                    @click="emit('open', file.fileId, file.filename)"
                >
                    <i class="pi pi-file bubble-file-icon" />
                    <span class="bubble-file-text">
                        <span class="bubble-file-name">{{
                            file.filename
                        }}</span>
                        <span v-if="fileMeta(file)" class="bubble-file-meta">{{
                            fileMeta(file)
                        }}</span>
                    </span>
                    <i class="pi pi-external-link bubble-file-open-icon" />
                </button>
                <button
                    type="button"
                    class="bubble-file-download"
                    :title="`Download ${file.filename}`"
                    aria-label="Download file"
                    @click="emit('download', file.fileId, file.filename)"
                >
                    <i class="pi pi-download" />
                </button>
            </div>
        </div>
        <div v-if="entry.references?.length" class="references">
            <div
                v-for="(ref, i) in entry.references"
                :key="i"
                class="reference-chip"
            >
                <span v-if="ref.type === 'entity'" class="ref-entity">
                    {{ ref.entity_type }}: {{ ref.entity_name }}
                </span>
            </div>
        </div>
    </div>

    <!-- Agent action (activity log item — silent, not a chat message) -->
    <div
        v-else-if="entry.type === 'agent_action'"
        class="agent-action-row"
        :class="entry.action_detail?.success === false ? 'action-failed' : ''"
    >
        <i
            :class="[
                'action-icon',
                actionIcon(entry.action_detail?.action_type),
            ]"
        />
        <RouterLink
            v-if="entityLinkFor(entry)"
            class="action-detail action-link"
            :to="entityLinkFor(entry)!"
            @mouseenter="showEntityCard($event, entry)"
            @mouseleave="hideEntityCard"
            >{{ actionLabel(entry) }}</RouterLink
        >
        <span v-else class="action-detail">{{ actionLabel(entry) }}</span>
        <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
        <div
            v-if="entityCard"
            class="entity-card-pop"
            :style="entityCard.style"
            data-testid="thread-entity-card"
        >
            <GenericCard
                :entityId="entityCard.entityId"
                :entityType="entityCard.entityType"
            />
        </div>
    </div>

    <!-- System event (activity feed only) -->
    <div v-else-if="entry.type === 'system_event'" class="agent-action-row">
        <i class="action-icon pi pi-info-circle" />
        <span class="action-detail">{{ entry.content }}</span>
        <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
    </div>

    <!-- Agent response -->
    <div
        v-else-if="entry.type === 'agent_response'"
        class="entry-bubble agent-bubble"
    >
        <div class="bubble-header">
            <span class="source-name">{{ entry.source.name }}</span>
            <span class="timestamp">{{ formatTime(entry.timestamp) }}</span>
        </div>
        <ThreadMarkdown
            class="bubble-content"
            :content="stripAgentEnvelope(entry.content ?? '')"
        />
        <div v-if="entry.references?.length" class="references">
            <div
                v-for="(ref, i) in entry.references"
                :key="i"
                class="reference-chip"
            >
                <span v-if="ref.type === 'entity'" class="ref-entity">
                    {{ ref.entity_type }}: {{ ref.entity_name }}
                </span>
                <span v-else-if="ref.type === 'thread'" class="ref-thread">
                    Thread: {{ ref.title }}
                    <Tag :value="ref.status ?? ''" severity="secondary" />
                </span>
            </div>
        </div>
        <div v-if="showApprovalButtons(entry, index)" class="approval-actions">
            <Button
                label="Confirm"
                icon="pi pi-check"
                severity="success"
                outlined
                size="small"
                @click="emit('confirm', entry.id)"
            />
            <Button
                label="Cancel"
                icon="pi pi-times"
                severity="danger"
                outlined
                size="small"
                @click="emit('cancel', entry.id)"
            />
        </div>
    </div>

    <!-- Resolution -->
    <div v-else-if="entry.type === 'resolution'" class="entry-resolution">
        <div
            class="resolution-banner"
            :class="entry.content?.includes('accept') ? 'accepted' : 'rejected'"
        >
            <i
                :class="
                    entry.content?.includes('accept')
                        ? 'pi pi-check-circle'
                        : 'pi pi-times-circle'
                "
            />
            <div class="resolution-details">
                <span class="resolution-action">{{
                    humanizeResolutionContent(entry.content ?? "")
                }}</span>
                <span class="resolution-by">by {{ entry.source.name }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Tag from "primevue/tag";
import { defineAsyncComponent, ref } from "vue";

import ThreadMarkdown from "@/components/Thread/ThreadMarkdown.vue";
import type { TimelineFile } from "@/components/Thread/ThreadTimeline.vue";
import {
    PROPOSAL_CANCEL_MESSAGE,
    PROPOSAL_CONFIRM_MESSAGE,
    humanSize,
    stripAgentEnvelope,
} from "@/components/Thread/thread-content";
import { humanizeResolutionContent } from "@/components/Thread/thread-status";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { entityRouteFor } from "@/lib/entity-route";
import { EntityType } from "@/lib/openapi/gen/types.gen";
import { withThreadRevision } from "@/lib/thread-revision-link";
import type { ThreadEntrySchema } from "@/stores/threads-store";
import { useUserStore } from "@/stores/user-store";

const props = withDefaults(
    defineProps<{
        entry: ThreadEntrySchema;
        // The draft this timeline belongs to. Load-bearing for entity links:
        // the thread detail page shows a thread without entering its branch,
        // so the release store is empty and the link would lose the draft.
        threadId?: string;
        // The entry's index within the full `entries` list — the adjacency logic
        // (approval buttons, proposal replies) keys off it.
        index: number;
        entries: ThreadEntrySchema[];
        // Files sent with this message, shown inside its bubble (open / download).
        files?: TimelineFile[];
        threadOpen?: boolean;
        respondingProposalId?: string | null;
    }>(),
    { files: () => [] }
);

const emit = defineEmits<{
    confirm: [entryId: string];
    cancel: [entryId: string];
    open: [fileId: string, filename: string];
    download: [fileId: string, filename: string];
}>();

// Async: it drags in the whole entity-info stack, and only a hover needs it.
const GenericCard = defineAsyncComponent(
    () => import("@/components/EntityInfo/GenericCard.vue")
);

const { organization } = useOrganizationManager();
const { stek } = useStekManager();

// The backend parses the id back out of this string, so it cannot be dropped at
// the source. Match the exact id — a name can contain brackets too.
const actionLabel = (entry: ThreadEntrySchema) => {
    const label = entry.action_detail?.detail ?? entry.content;
    const id = entry.action_detail?.entity_id;
    return id ? label.replace(` (${id})`, "") : label;
};

// These pages only mount in the IT landscape; with it off the link would just
// redirect back to business.
const DEV_MODE_TYPES: string[] = [
    EntityType.APPLICATION,
    EntityType.APPLICATION_ENTITY,
];

// withThreadRevision keeps the link on the draft branch, the only place the
// entity exists until the thread is accepted.
const entityLinkFor = (entry: ThreadEntrySchema) => {
    const type = entry.action_detail?.entity_type;
    if (
        type &&
        DEV_MODE_TYPES.includes(type) &&
        stek.value?.dev_mode_enabled === false
    ) {
        return undefined;
    }
    const route = entityRouteFor(type, entry.action_detail?.entity_id, {
        orgName: organization.value?.name,
        stekName: stek.value?.name,
    });
    return route ? withThreadRevision(route, props.threadId) : undefined;
};

// GenericCard reads these three from the worktree, already at the thread
// revision. Other types would look on main and find nothing.
const CARD_TYPES: string[] = [
    EntityType.DOMAIN,
    EntityType.PRODUCT,
    EntityType.PROCESS,
];

const entityCard = ref<{
    entityId: string;
    entityType: EntityType;
    style: Record<string, string>;
} | null>(null);

const showEntityCard = (event: MouseEvent, entry: ThreadEntrySchema) => {
    const { entity_type: type, entity_id: id } = entry.action_detail ?? {};
    if (!type || !id || !CARD_TYPES.includes(type)) return;
    const row = (event.currentTarget as HTMLElement).getBoundingClientRect();
    entityCard.value = {
        entityId: id,
        entityType: type as EntityType,
        style: { top: `${row.bottom + 6}px`, left: `${row.left}px` },
    };
};

const hideEntityCard = () => {
    entityCard.value = null;
};

// "PDF · 2 KB" style meta from the file extension and byte size.
const fileMeta = (file: TimelineFile): string => {
    const ext = file.filename.includes(".")
        ? file.filename.split(".").pop()!.toUpperCase()
        : "";
    return [ext, humanSize(file.size)].filter(Boolean).join(" · ");
};

// Only on a still-live proposal — stale once anything follows or the thread closes.
const showApprovalButtons = (entry: ThreadEntrySchema, index: number) =>
    props.threadOpen === true &&
    entry.type === "agent_response" &&
    entry.message_reason === "approval_request" &&
    index === props.entries.length - 1 &&
    entry.id !== props.respondingProposalId;

// Button-posted reply to the proposal right before it; hand-typed replies stay bubbles.
const proposalReplyKind = (
    entry: ThreadEntrySchema,
    index: number
): "confirmed" | "cancelled" | null => {
    if (entry.type !== "contribution") return null;
    const prev = props.entries[index - 1];
    if (
        !prev ||
        prev.type !== "agent_response" ||
        prev.message_reason !== "approval_request"
    ) {
        return null;
    }
    if (entry.content === PROPOSAL_CONFIRM_MESSAGE) return "confirmed";
    if (entry.content === PROPOSAL_CANCEL_MESSAGE) return "cancelled";
    return null;
};

const userStore = useUserStore();

const proposalReplyLabel = (entry: ThreadEntrySchema, index: number) => {
    const kind = proposalReplyKind(entry, index);
    const own = entry.source.id === userStore.user?.identity;
    if (kind === "confirmed") {
        return own ? "You confirmed this" : `Confirmed by ${entry.source.name}`;
    }
    return own ? "You cancelled this" : `Cancelled by ${entry.source.name}`;
};

const actionIcon = (actionType?: string | null) => {
    if (!actionType) return "pi pi-bolt";
    if (actionType.startsWith("create")) return "pi pi-plus-circle";
    if (actionType.startsWith("delete")) return "pi pi-minus-circle";
    if (actionType.startsWith("update")) return "pi pi-pencil";
    return "pi pi-bolt";
};

const formatTime = (timestamp: string | Date) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;

    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
</script>

<style scoped>
.action-link {
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus-visible {
        text-decoration: underline;
    }
}

/* Fixed, so the row's overflow cannot clip it. */
.entity-card-pop {
    position: fixed;
    z-index: 1200;
    width: 18rem;
    pointer-events: none;
}

.entry-bubble {
    border-radius: 12px;
    padding: 0.75rem 1rem;
    max-width: 85%;
}

.contribution-bubble {
    background: var(--p-surface-100);
    align-self: flex-start;
}

.agent-bubble {
    background: var(--p-primary-50);
    border: 1px solid var(--p-primary-100);
    align-self: flex-start;
}

.bubble-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.source-name {
    font-weight: 600;
    font-size: 0.85rem;
}

.source-tag {
    font-size: 0.7rem;
}

.timestamp {
    font-size: 0.75rem;
    color: var(--p-text-muted-color);
    margin-left: auto;
}

.bubble-content {
    font-size: 0.95rem;
}

.bubble-content :deep(.thread-html-snippet) {
    margin: 0.5em 0;
    max-width: 100%;
}

.bubble-content :deep(p) {
    margin: 0.5em 0;
}

.bubble-content :deep(p:first-child) {
    margin-top: 0;
}

.bubble-content :deep(p:last-child) {
    margin-bottom: 0;
}

.bubble-content :deep(a) {
    color: var(--p-primary-color);
    text-decoration: underline;
    overflow-wrap: anywhere;
}

.bubble-content :deep(code) {
    background: var(--p-surface-200);
    padding: 0.1em 0.3em;
    font-size: 0.85em;
}

.bubble-content :deep(ul),
.bubble-content :deep(ol) {
    margin: 0.5em 0;
    padding-left: 1.4em;
    list-style-position: outside;
}

.bubble-content :deep(ul) {
    list-style-type: disc;
}

.bubble-content :deep(ol) {
    list-style-type: decimal;
}

.bubble-content :deep(li) {
    margin: 0.2em 0;
}

.bubble-content :deep(h1),
.bubble-content :deep(h2),
.bubble-content :deep(h3),
.bubble-content :deep(h4) {
    margin: 1.1em 0 0.35em;
    font-weight: 700;
    line-height: 1.3;
}

.bubble-content :deep(h1),
.bubble-content :deep(h2) {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--p-text-muted-color);
    padding-bottom: 0.3em;
    border-bottom: 1px solid var(--p-content-border-color);
}

.bubble-content :deep(h3),
.bubble-content :deep(h4) {
    font-size: 1rem;
}

.bubble-content :deep(:is(h1, h2, h3, h4):first-child) {
    margin-top: 0;
}

.bubble-content :deep(em) {
    color: var(--p-text-muted-color);
    font-size: 0.85em;
}

.bubble-content :deep(hr) {
    margin: 0.9em 0;
    border: 0;
    border-top: 1px solid var(--p-content-border-color);
}

/* Wrap long/unbreakable code content instead of overflowing the narrow bubble.
   Written as top-level scoped selectors: the nested `.bubble-content { :deep() }`
   form above does not compile to a working selector in this build. */
.bubble-content :deep(pre) {
    max-width: 100%;
    margin: 0.25em 0;
    padding: 0.5em 0.75em;
    background: var(--p-surface-200);
    border-radius: 6px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.bubble-content :deep(pre) code {
    background: none;
    padding: 0;
}

.bubble-content :deep(code) {
    overflow-wrap: anywhere;
}

.references {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

/* Files sent with the message: a card inside the bubble, download-only. */
.bubble-files {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.5rem;
}
.bubble-file {
    display: flex;
    align-items: stretch;
    width: 100%;
    border: 1px solid var(--p-surface-300);
    background: var(--p-surface-0);
    color: var(--p-text-color);
}
/* The file info doubles as the "open in a new tab" button. */
.bubble-file-open {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex: 1;
    min-width: 0;
    padding: 0.5rem 0.6rem;
    font: inherit;
    text-align: left;
    cursor: pointer;
    border: none;
    background: transparent;
    color: inherit;
}
.bubble-file-open:hover {
    background: var(--p-surface-50);
}
.bubble-file-icon {
    font-size: 1.1rem;
    color: var(--p-primary-color);
    flex: none;
}
.bubble-file-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}
.bubble-file-name {
    font-size: 0.85rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.bubble-file-meta {
    font-size: 0.72rem;
    color: var(--p-text-muted-color);
    font-variant-numeric: tabular-nums;
}
.bubble-file-open-icon {
    font-size: 0.8rem;
    color: var(--p-text-muted-color);
    flex: none;
}
.bubble-file-download {
    display: inline-flex;
    align-items: center;
    padding: 0 0.6rem;
    font: inherit;
    cursor: pointer;
    border: none;
    border-left: 1px solid var(--p-surface-200);
    background: transparent;
    color: var(--p-text-muted-color);
}
.bubble-file-download:hover {
    background: var(--p-surface-50);
    color: var(--p-primary-color);
}

.approval-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.75rem;
}

.proposal-reply-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 500;
}

.proposal-reply-row.confirmed {
    border-left: 2px solid var(--p-green-300);
    color: var(--p-green-700);
}

.proposal-reply-row.cancelled {
    border-left: 2px solid var(--p-red-300);
    color: var(--p-red-600);
}

.proposal-reply-text {
    flex: 1;
}

.reference-chip {
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 6px;
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
}

.agent-action-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    border-left: 2px solid var(--p-primary-200);
    font-size: 0.85rem;
    color: var(--p-text-muted-color);
}

.agent-action-row.action-failed {
    border-left-color: var(--p-red-300);
    color: var(--p-red-600);
}

.action-icon {
    font-size: 0.8rem;
    flex-shrink: 0;
}

.agent-action-row:not(.action-failed) .action-icon {
    color: var(--p-primary-400);
}

.action-detail {
    flex: 1;
}

.resolution-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-weight: 500;
}

.resolution-banner.accepted {
    background: var(--p-green-50);
    border: 1px solid var(--p-green-200);
    color: var(--p-green-700);
}

.resolution-banner.rejected {
    background: var(--p-red-50);
    border: 1px solid var(--p-red-200);
    color: var(--p-red-700);
}

.resolution-banner i {
    font-size: 1.25rem;
}

.resolution-details {
    display: flex;
    flex-direction: column;
}

.resolution-action {
    font-size: 0.95rem;
}

.resolution-by {
    font-size: 0.8rem;
    opacity: 0.8;
}
</style>
